import React, {useState} from 'react';
import {Button, Tooltip} from '@spaced-out/ui-design-system';
import {MicAccessory} from 'src/components/agentic-ui/voice/mic-accessory';
import {useElevation} from 'src/components/agentic-ui/elevation';
import type {MicDevice, MicPermission} from 'src/types/speech-to-text';
import css from './mic-control.css';


export interface MicControlProps {
  permission: MicPermission;
  devices: MicDevice[];
  /** '' = system default. */
  selectedDeviceId: string;
  onSelectDevice: (deviceId: string) => void;
  /** Begin voice capture. */
  onMicClick: () => void;
  micDisabled?: boolean;
  /** aria/label text — pass already-translated (this component does no i18n). */
  micLabel: string;
  defaultMicLabel: string;
  /** Tooltip shown before access is granted (will prompt on click). */
  micPromptTooltip: string;
  /** Tooltip shown when access was denied/blocked. */
  micDeniedTooltip: string;
}

/**
 * Mic button plus its device picker. Once permission is granted the picker is
 * revealed on hover of the group (and stays mounted while its menu is open so
 * the user can move onto it). Before access is granted, hovering the button
 * explains what will happen (prompt) or how to unblock it (denied).
 */
export function MicControl({
  permission,
  devices,
  selectedDeviceId,
  onSelectDevice,
  onMicClick,
  micDisabled = false,
  micLabel,
  defaultMicLabel,
  micPromptTooltip,
  micDeniedTooltip,
}: MicControlProps) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Tooltip portals to <body>; lift it above the panel's stacking context.
  const tooltipElevation = useElevation('tooltip');

  const micButton = (
    <Button
      type="ghost"
      size="small"
      iconLeftName="microphone"
      disabled={micDisabled}
      onClick={onMicClick}
      aria-label={micLabel}
    />
  );

  // Granted: reveal the device picker on hover; keep it while its menu is open.
  if (permission === 'granted') {
    return (
      <div
        className={css.micGroup}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {(hovered || menuOpen) && (
          <MicAccessory
            devices={devices}
            selectedDeviceId={selectedDeviceId}
            onSelectDevice={onSelectDevice}
            defaultMicLabel={defaultMicLabel}
            onMenuOpenChange={setMenuOpen}
          />
        )}
        {micButton}
      </div>
    );
  }

  // Not granted: explain on hover. Denied gets the "blocked" copy; everything
  // else (prompt/unknown) gets the first-time "we'll ask" copy.
  const tooltipBody =
    permission === 'denied' ? micDeniedTooltip : micPromptTooltip;
  return (
    <div className={css.micGroup}>
      <Tooltip
        body={tooltipBody}
        bodyMaxLines={3}
        placement="top"
        elevation={tooltipElevation}
      >
        {micButton}
      </Tooltip>
    </div>
  );
}
